import Error404 from '@auth/views/Error404'
import {
  ContentWrapper,
  CookieConsent,
  CustomPreloader,
  ErrorFallback,
  Header,
  MaintenanceModal,
  ModalDialog,
  Otp
} from '@components'
import settings from '@config/settings'
import theme from '@config/theme'
import { COOKIE_NAME, MODAL_SIZE, TOAST_CONFIG } from '@constants'
import { nonProtectedRoutes, protectedRoutes } from '@routes'
import {
  LoginStatuses,
  PrivateRoute,
  PublicRoute,
  selectCurrentUserStatus,
  useAppInitializerQry,
  useAppSelector,
  useAuthQry,
  useLazyErrorLoggingQuery
} from 'mmfintech-backend-api'
import { GlobalContextProvider, isMobileDevice, isValidObject, OtpContext } from 'mmfintech-commons'
import { type ReactNode, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { CookiesProvider, useCookies } from 'react-cookie'
import { ErrorBoundary } from 'react-error-boundary'
import { Toaster } from 'react-hot-toast'
import { withTranslation } from 'react-i18next'
import { Route, Switch } from 'react-router-dom'
import { ThemeProvider } from 'styled-components'

import './App.scss'

function AppInner() {
  useAppInitializerQry(settings.languages)
  const { isAuthFetching } = useAuthQry()

  const [cookies, setCookie] = useCookies([COOKIE_NAME.CONSENT])
  const [cookieConsent, setCookieConsent] = useState(null)

  const loginStatus = useAppSelector(selectCurrentUserStatus)
  const queryChallenge = useAppSelector(state => state.challenge)

  useEffect(() => {
    setCookieConsent(cookies[COOKIE_NAME.CONSENT])
  }, [cookies])

  useEffect(() => {
    if (typeof queryChallenge === 'object' && 'challengeId' in queryChallenge) {
      modalShow({
        options: {
          size: MODAL_SIZE.MEDIUM,
          transparent: true,
          closeOnClickOutside: false,
          closeOnEscape: false
        },
        content: <Otp />
      })
    } else {
      modalVisible && modalHide()
    }
  }, [queryChallenge])

  const [modalContent, setModalContent] = useState<ReactNode>(null)
  const [modalOptions, setModalOptions] = useState(null)
  const [modalVisible, setModalVisible] = useState<boolean>(false)

  const [otpOnSuccess, setOtpOnSuccess] = useState(null)
  const [otpOnError, setOtpOnError] = useState(null)

  const modalHide = useCallback(() => {
    setModalVisible(false)
    setModalContent(null)
    setModalOptions(null)
  }, [])

  const modalShow = useCallback(({ options, content }) => {
    setModalContent(content)
    setModalOptions(options)
    setModalVisible(true)
  }, [])

  const globalContext = useMemo(() => ({ modalHide, modalShow }), [modalHide, modalShow])

  const otpContext = useMemo(
    () => ({ otpOnSuccess, setOtpOnSuccess, otpOnError, setOtpOnError }),
    [otpOnError, otpOnSuccess]
  )

  return isAuthFetching ? (
    <CustomPreloader />
  ) : (
    <CookiesProvider>
      <ThemeProvider theme={theme}>
        <MaintenanceModal />
        <GlobalContextProvider context={globalContext}>
          <OtpContext.Provider value={otpContext}>
            <ContentWrapper>
              {loginStatus === LoginStatuses.LOGGED_IN && <Header />}

              <Switch>
                {nonProtectedRoutes.map(definition => (
                  <PublicRoute key={definition.path} exact {...definition} />
                ))}
                {protectedRoutes.map(definition => {
                  // console.log(definition)
                  return <PrivateRoute key={definition.path} exact {...definition} />
                })}
                <Route path='*' component={Error404} />
              </Switch>

              {isValidObject(cookieConsent) || isMobileDevice() ? null : <CookieConsent setCookie={setCookie} />}

              <ModalDialog content={modalContent} options={modalOptions} visible={modalVisible} onClose={modalHide} />
            </ContentWrapper>

            <Toaster
              position={TOAST_CONFIG.POSITION}
              containerStyle={{
                top: TOAST_CONFIG.CONTAINER_TOP,
                zIndex: TOAST_CONFIG.Z_INDEX
              }}
              toastOptions={{
                className: '',
                style: {
                  color: '#000000',
                  fontSize: '1.4rem',
                  fontStyle: 'normal',
                  padding: '1.5rem',
                  borderRadius: '0'
                },
                success: {}
              }}
            />
          </OtpContext.Provider>
        </GlobalContextProvider>
      </ThemeProvider>
    </CookiesProvider>
  )
}

const ThisApp = withTranslation()(AppInner)

function App() {
  const [logError] = useLazyErrorLoggingQuery()

  return (
    <Suspense
      fallback={
        <div className='app-preloader-wrapper'>
          <CustomPreloader />
        </div>
      }
    >
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(err, componentStack) => {
          return logError({
            level: 'ERROR',
            componentStack: componentStack?.componentStack ?? String(componentStack),
            message: err.toString()
          })
        }}
        onReset={() => {
          window.location.replace('/')
        }}
      >
        <ThisApp />
      </ErrorBoundary>
    </Suspense>
  )
}

export default App
